import styled from '@emotion/styled';
import { useSnackbar } from 'notistack';
import type { CSSProperties, DragEvent, FunctionComponent } from 'react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import { Button } from '~/ui/core/button/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconChevronDownSmallLight, IconFile, IconFolder, IconUpload } from '~/ui/core/icons';
import { Spinner } from '~/ui/core/spinner/Spinner';

const FileDropZoneLabel = styled.p(({ theme }) => ({
    color: theme.colors.gray[800],
    fontSize: theme.typography.body.S.medium.fontSize,
    margin: 0,
    textAlign: 'center',
}));

const StyledUploadIcon = styled(IconUpload)(({ theme }) => ({
    color: theme.colors.gray[500],
    height: theme.spacing['5xl'],
    width: theme.spacing['5xl'],
}));

const Important = styled.span(({ theme }) => ({
    color: theme.colors.black[800],
    fontSize: theme.typography.body.S.medium.fontSize,
    fontWeight: theme.typography.body.S.medium.fontWeight,
    lineHeight: theme.typography.body.S.medium.lineHeight,
}));

const FileDropZoneContainer = styled.div<{ readonly isDragActive: boolean }>(({ isDragActive, theme }) => {
    return {
        alignItems: 'center',
        border: `1.5px dashed ${isDragActive ? theme.colors.info[500] : theme.colors.gray[300]}`,
        borderRadius: theme.borderRadius.m,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing.l,
        justifyContent: 'center',
        marginBottom: theme.spacing['4xl'],
        marginTop: theme.spacing['4xl'],
        padding: theme.spacing['4xl'],
    };
});

const HiddenInput = styled.input({
    display: 'none',
});

const DropZoneContent = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.l,
}));

const ButtonContent = styled.span(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing.s,
}));

type FileDropZoneProps = {
    readonly accept?: string;
    readonly isUploadInProgress?: boolean;
    readonly multiple?: boolean;
    readonly onSelectingFiles: (files: ReadonlyMap<string, File>) => Promise<void> | void;
    readonly style?: CSSProperties;
};

const ALLOWED_EXTENSIONS = ['pdf', 'txt', 'md', 'html', 'docx', 'doc', 'xlsx', 'xls', 'csv'];
const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_FILES_PER_BATCH_UPLOAD_COUNT = 500;

const getEffectiveFileName = (file: File): string => {
    const relativePath = (file as File & { webkitRelativePath?: string }).webkitRelativePath;

    if (relativePath !== undefined && relativePath.length > 0) {
        const pathParts = relativePath.split('/');
        const pathWithoutRoot = pathParts.slice(1).join('/');

        return pathWithoutRoot.length > 0 ? pathWithoutRoot : file.name;
    }

    return file.name;
};

const filesToMap = (files: readonly File[]): ReadonlyMap<string, File> => {
    const map = new Map<string, File>();
    for (const file of files) {
        map.set(getEffectiveFileName(file), file);
    }

    return map;
};

export const FileDropZone: FunctionComponent<FileDropZoneProps> = ({
    accept = ALLOWED_EXTENSIONS.map((it) => `.${it}`).join(','),
    isUploadInProgress,
    multiple = true,
    onSelectingFiles: onFilesSelected,
    style,
}) => {
    const { translate } = useContext(ApplicationContext);
    const { enqueueSnackbar } = useSnackbar();
    const [isDragActive, setIsDragActive] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const folderInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (folderInputRef.current !== null) {
            folderInputRef.current.setAttribute('webkitdirectory', '');
            folderInputRef.current.setAttribute('directory', '');
        }
    }, []);

    const isValidFilesLength = useCallback(
        (files: readonly File[]): boolean => {
            if (files.length > MAX_FILES_PER_BATCH_UPLOAD_COUNT) {
                enqueueSnackbar(
                    translate(
                        'pages.job-architecture-v2.knowledge-base.max-number-of-files-per-batch-exceeded-exception',
                        { count: MAX_FILES_PER_BATCH_UPLOAD_COUNT },
                    ),
                    { variant: 'error' },
                );

                return false;
            }

            return files.length > 0;
        },
        [enqueueSnackbar, translate],
    );

    const filterValidFiles = useCallback(
        (files: readonly File[]): File[] => {
            const oversizedFile = files.find((file) => file.size > MAX_FILE_SIZE_BYTES);
            if (oversizedFile !== undefined) {
                enqueueSnackbar(
                    translate('pages.job-architecture-v2.knowledge-base.file-too-large-exception', {
                        max_size: MAX_FILE_SIZE_MB,
                        name: oversizedFile.name,
                    }),
                    { variant: 'error' },
                );

                return [];
            }

            return files.filter((file) => {
                const fileName = file.name.toLowerCase();
                const lastDotIndex = fileName.lastIndexOf('.');

                if (lastDotIndex <= 0) {
                    return false;
                }

                const extension = fileName.slice(lastDotIndex + 1);

                return ALLOWED_EXTENSIONS.includes(extension);
            });
        },
        [enqueueSnackbar, translate],
    );

    const handleDragEnter = useCallback(
        (e: DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();
            if (isUploadInProgress === true) {
                return;
            }
            setIsDragActive(true);
        },
        [isUploadInProgress],
    );

    const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    }, []);

    const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback(
        (e: DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragActive(false);

            if (isUploadInProgress === true) {
                return;
            }

            const files = filterValidFiles(Array.from(e.dataTransfer.files));
            if (isValidFilesLength(files)) {
                onFilesSelected(filesToMap(files));
            }
        },
        [filterValidFiles, isUploadInProgress, isValidFilesLength, onFilesSelected],
    );

    const handleFilesClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFolderClick = useCallback(() => {
        folderInputRef.current?.click();
    }, []);

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (isUploadInProgress === true) {
                return;
            }

            const files = filterValidFiles(Array.from(e.target.files ?? []));
            if (isValidFilesLength(files)) {
                onFilesSelected(filesToMap(files));
            }
            e.target.value = '';
        },
        [filterValidFiles, isUploadInProgress, isValidFilesLength, onFilesSelected],
    );

    return (
        <FileDropZoneContainer
            isDragActive={isDragActive}
            style={style}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            {isUploadInProgress !== true && (
                <DropZoneContent>
                    <StyledUploadIcon />
                    <FileDropZoneLabel>
                        <Important>{translate('pages.job-architecture-v2.knowledge-base.drop-zone-label')}</Important>
                        <br />
                        {translate('pages.job-architecture-v2.knowledge-base.drop-zone-supported-formas', {
                            supported_formats: ALLOWED_EXTENSIONS.join(', ').toUpperCase(),
                        })}
                    </FileDropZoneLabel>
                    <DropdownMenu>
                        <DropdownMenuTrigger>
                            <Button variant="primary">
                                <ButtonContent>
                                    {translate('pages.job-architecture-v2.knowledge-base.upload-button')}
                                    <IconChevronDownSmallLight height={16} width={16} />
                                </ButtonContent>
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="center" sideOffset={4}>
                            <DropdownMenuItem onSelect={handleFilesClick}>
                                <IconFile height={16} width={16} />
                                {translate('pages.job-architecture-v2.knowledge-base.files')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={handleFolderClick}>
                                <IconFolder height={16} width={16} />
                                {translate('pages.job-architecture-v2.knowledge-base.folder')}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </DropZoneContent>
            )}
            {isUploadInProgress === true && (
                <FileDropZoneLabel>
                    <Spinner />
                </FileDropZoneLabel>
            )}
            <HiddenInput
                ref={fileInputRef}
                accept={accept}
                disabled={isUploadInProgress === true}
                multiple={multiple}
                type="file"
                onChange={handleInputChange}
            />
            <HiddenInput
                ref={folderInputRef}
                accept={accept}
                disabled={isUploadInProgress === true}
                multiple={true}
                type="file"
                onChange={handleInputChange}
            />
        </FileDropZoneContainer>
    );
};
